"use client";
import { useState, useRef } from "react";
import clsx from "clsx";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useClientContext } from "@/shared/components/Context";

import s from "./styles.module.css";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const getIsAlternateHightlight = (chunk: string): boolean =>
  chunk[0] === "[" && chunk[chunk.length - 1] === "]";

const clearChunk = (chunk: string): string => chunk.slice(1, -1);

interface Props {
  intro?: string;
  text: string;
  className?: string;
}

export default function TextReveal({ text, intro = "", className }: Props) {
  const clientContext = useClientContext();
  const [countToHightlight, setCountToHightlight] = useState(0);
  const delimeter = useRef<string>(" ");
  const ref = useRef<HTMLElement>(null);

  const splittedTextRef = useRef(text.split(delimeter.current));
  const splittedText = splittedTextRef.current;

  // Word-by-word highlight driven by GSAP ScrollTrigger. start/end return
  // absolute scroll positions (px).
  useGSAP(
    () => {
      const section = ref.current;
      if (!section) return;

      const st = ScrollTrigger.create({
        trigger: section,
        start: () => clientContext.welcomeHeight - window.innerHeight / 3,
        end: () =>
          section.offsetHeight -
          window.innerHeight / 1.4 +
          clientContext.welcomeHeight,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          setCountToHightlight(self.progress * splittedText.length);
        },
      });

      return () => st.kill();
    },
    {
      dependencies: [clientContext.welcomeHeight, splittedText.length],
      scope: ref,
    }
  );

  return (
    <section ref={ref} className={clsx(s.generalDesciption, className)}>
      {intro && <h3 className={clsx(s.intro)}>{intro}</h3>}
      <p className={s.generalDesciptionText}>
        {splittedText.map((chunk, i) => {
          const isAlternateHightlight = getIsAlternateHightlight(chunk);

          const hightlighedClassName = isAlternateHightlight
            ? s.alternateHighlightedChunk
            : s.highlightedChunk;

          const formattedChunk = isAlternateHightlight
            ? clearChunk(chunk)
            : chunk;

          return (
            <span
              key={i}
              className={clsx(s.chunk, {
                [hightlighedClassName]: i < Math.floor(countToHightlight),
              })}
            >
              {formattedChunk}
              {delimeter.current}
            </span>
          );
        })}
      </p>
    </section>
  );
}
